// What a survey-end page may show, decided ONLY from the backend's answer to
// GetUserOneAssignedSurveyCallback — never from the URL path. The path says
// what the survey provider claims happened; only the backend knows whether
// it was recorded.
//
//   redirect  go to `url` (partner passback, country thank-you page, or the
//             partner hold page for a closed survey)
//   thanks    show the thank-you page; `status` is the backend status to
//             display ('Completed', 'Terminated', ...). For a repeat hit it
//             is the STORED status, not the one in the URL.
//   invalid   "This link is not valid or has expired." — nothing else
//
// Rules:
//   recorded === true                     -> redirect if a URL came back, else thanks
//   reason already_recorded|already_final -> redirect if a URL came back, else thanks (stored status)
//   recorded === false + partner hold URL -> redirect (closed-survey partner)
//   anything else, or no/odd response     -> invalid

export type CallbackOutcome =
    | { kind: 'redirect'; url: string }
    | { kind: 'thanks'; status: string }
    | { kind: 'invalid'; reason: string };

const REPEAT_REASONS = ['already_recorded', 'already_final'];
const HOLD_PAGE = '/#/partner-survey-hold';

export function decideCallbackOutcome(result: any, requestedStatus: string): CallbackOutcome {
    if (!result || typeof result !== 'object') {
        return { kind: 'invalid', reason: 'no_response' };
    }
    const url: string = String(result.redirectUrl || result.url || '');
    const reason: string = String(result.reason || '');

    if (result.recorded === true) {
        return url ? { kind: 'redirect', url } : { kind: 'thanks', status: requestedStatus };
    }
    if (result.recorded === false && REPEAT_REASONS.includes(reason)) {
        if (url) return { kind: 'redirect', url };
        const stored = String(result.recordedStatus || result.status || '');
        return stored ? { kind: 'thanks', status: stored } : { kind: 'invalid', reason };
    }
    if (result.recorded === false && url.includes(HOLD_PAGE)) {
        return { kind: 'redirect', url };
    }
    return { kind: 'invalid', reason: reason || 'unrecognised_response' };
}

export const INVALID_LINK_MESSAGE = 'This link is not valid or has expired.';

// Display metadata per backend status. 'Verisoul Blocked' is a quality
// termination to the respondent.
export const STATUS_DISPLAY: Record<string, { title: string; bg: string; fg: string; thanks: string }> = {
    'Completed':          { title: 'Completed',          bg: '#28a745', fg: '#ffffff', thanks: 'You have completed the survey. Thank you for taking part.' },
    'Terminated':         { title: 'Terminated',         bg: '#dc3545', fg: '#ffffff', thanks: 'Unfortunately you did not qualify for this survey. Thank you for your time.' },
    'Over Quota':         { title: 'Over Quota',         bg: '#ffc107', fg: '#212529', thanks: 'This survey has already received all the responses it needs for your group. Thank you for your time.' },
    'Quality Terminated': { title: 'Quality Terminated', bg: '#6c757d', fg: '#ffffff', thanks: 'Your response could not be accepted for this survey. Thank you for your time.' },
};
export const displayFor = (status: string) =>
    STATUS_DISPLAY[status === 'Verisoul Blocked' ? 'Quality Terminated' : status] || null;
